import { useCallback, useMemo } from "react";

import { cn } from "@/common/utils/css.utils";
import EmptyStarIcon from "@whoppah/icons/24/EmptyStar";
import HalfStarIcon from "@whoppah/icons/24/HalfStar";
import StarIcon from "@whoppah/icons/24/Star";

const STARS: Record<
  number,
  ({
    className,
    onClick,
  }: {
    className: string;
    onClick?: () => void;
  }) => JSX.Element
> = {
  [1]: StarIcon,
  [0.5]: HalfStarIcon,
  [0]: EmptyStarIcon,
};

type RatingProps = {
  value?: number;
  maxValue?: number;
  className?: string;
  onChange?: (value: number) => void;
};

const Rating = ({
  value = 0,
  maxValue = 5,
  className,
  onChange,
}: RatingProps) => {
  const getStarType = useCallback(
    (starNo: number) => {
      const fixedPart = Math.floor(value);
      const decimalPart = value - fixedPart;

      if (starNo <= fixedPart) {
        return 1;
      }

      if (starNo === fixedPart + 1 && decimalPart > 0) {
        return decimalPart <= 0.5 ? 0.5 : 1;
      }

      return 0;
    },
    [value]
  );

  const stars = useMemo(() => {
    const starsArray = [];

    for (let i = 1; i <= maxValue; i++) {
      starsArray.push(getStarType(i));
    }

    return starsArray;
  }, [getStarType, maxValue]);

  const handleStarClick = useCallback(
    (starIndex: number) => {
      if (onChange) {
        onChange?.(starIndex + 1);
      }
    },
    [onChange]
  );

  const isInteractive = onChange;

  return (
    <>
      {stars.map((star, index) => {
        const Icon = STARS[star];

        if (Icon) {
          return (
            <button
              key={`rating_${index}`}
              type="button"
              disabled={!onChange}
              onClick={() => handleStarClick(index)}
            >
              <Icon
                className={cn("text-[#f9d960]", className, {
                  "cursor-pointer hover:opacity-80": isInteractive,
                })}
              />
            </button>
          );
        }

        return null;
      })}
    </>
  );
};

export default Rating;
